import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { alpha, useTheme } from '@mui/material/styles';

// Visually‑rich, data‑dense User Insights card
// Props: { insightsData } where insightsData = { user_email, user_id, insights: [...], total_records, message }
export default function UserInsightsComponent({ insightsData }) {
  const items = insightsData?.insights || [];  // Backend uses "insights" not "items"
  const [showHistoricalModal, setShowHistoricalModal] = useState(false);

  if (!items || items.length === 0) {
    return (
      <Box className="mt-3">
        <Card sx={{ p: 2, borderRadius: 2, boxShadow: 'none', border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom color="text.secondary">
            No insights available yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This user doesn't have any activity data or risk scores calculated yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Insights will appear here once the user has sign-in activity, application access, or group memberships.
          </Typography>
        </Card>
      </Box>
    );
  }

  // Sort items by calculation date (most recent first)
  const sortedItems = [...items].sort((a, b) => {
    const dateA = new Date(a.calculation_date || a.created_at || 0);
    const dateB = new Date(b.calculation_date || b.created_at || 0);
    return dateB - dateA;
  });

  // Get the latest insight
  const latestInsight = sortedItems[0];
  const hasHistoricalData = sortedItems.length > 1;

  function getSeverity(level) {
    const l = String(level || '').toLowerCase();
    if (l === 'high') return 'error';
    if (l === 'moderate' || l === 'medium') return 'warning';
    return 'success'; // minimal/low/unknown
  }

  // Historical Data Modal Component
  function HistoricalDataModal() {
    const [timeFilter, setTimeFilter] = useState('all');

    // Filter data based on selected time period
    const getFilteredData = () => {
      if (timeFilter === 'all') return sortedItems;

      const daysToFilter = parseInt(timeFilter);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToFilter);

      return sortedItems.filter(item => {
        const itemDate = new Date(item.calculation_date || item.created_at || 0);
        return itemDate >= cutoffDate;
      });
    };

    const filteredItems = getFilteredData();

    // Prepare chart data for risk score trends
    const chartData = filteredItems.map((item, index) => ({
      date: item.calculation_date || item.created_at?.split('T')[0] || `Entry ${index + 1}`,
      riskScore: item.overall_risk_score || 0,
      dormantStatus: item.dormant_status ? 1 : 0,
      accessVelocity: item.access_change_velocity || 0,
      suspiciousActivity: item.suspicious_activity || 0,
      // Access metrics
      appCount: item.insight_details?.access_details?.app_count || 0,
      groupCount: item.insight_details?.access_details?.group_count || 0,
      recentChanges7d: item.insight_details?.access_details?.recent_changes_7d || 0,
      // Behavioral metrics
      rapidSignins24h: item.insight_details?.behavioral_details?.rapid_signins_24h || 0,
      offHoursSignins7d: item.insight_details?.behavioral_details?.off_hours_signins_7d || 0,
      // Additional metrics
      excessiveAccess: item.excessive_access || 0,
      behavioralScore: item.behavioral_score || 0
    })).reverse(); // Reverse to show chronological order

    // Individual metric chart component
    function MetricChart({ title, dataKey, color, unit = '', description }) {
      return (
        <Card sx={{ p: 2, borderRadius: 2, height: 280 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
            {description}
          </Typography>
          <Box sx={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  domain={['dataMin', 'dataMax']}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    fontSize: '0.8rem'
                  }}
                  formatter={(value) => [`${value}${unit}`, title]}
                />
                <Line
                  type="monotone"
                  dataKey={dataKey}
                  stroke={color}
                  strokeWidth={2}
                  dot={{ fill: color, strokeWidth: 1, r: 3 }}
                  activeDot={{ r: 5, stroke: color, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Card>
      );
    }

    return (
      <Dialog
        open={showHistoricalModal}
        onClose={() => setShowHistoricalModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Historical Insights & Risk Trends
            </Typography>
          </Box>
          <IconButton onClick={() => setShowHistoricalModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Time Period Filter */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Time Period
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[
                { value: '5', label: 'Past 5 Days' },
                { value: '10', label: 'Past 10 Days' },
                { value: '15', label: 'Past 15 Days' },
                { value: 'all', label: 'All Time' }
              ].map((option) => (
                <Button
                  key={option.value}
                  variant={timeFilter === option.value ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setTimeFilter(option.value)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 2
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
            <Chip
              label={`${filteredItems.length} entries`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>

        <DialogContent sx={{ pt: 2 }}>
          {/* Risk Score Trend Chart */}
          <Card sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineRoundedIcon color="primary" />
              Overall Risk Score Trend
            </Typography>
            <Box sx={{ height: 300, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 12 }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="riskScore"
                    stroke="#f44336"
                    strokeWidth={3}
                    dot={{ fill: '#f44336', strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7, stroke: '#f44336', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Card>

          {/* Individual Metric Charts Grid */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TimelineRoundedIcon color="primary" />
            Individual Metric Trends
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Access Metrics */}
            <Grid item xs={12} md={4}>
              <MetricChart
                title="App Count"
                dataKey="appCount"
                color="#2196f3"
                description="Number of applications user has access to"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricChart
                title="Group Count"
                dataKey="groupCount"
                color="#4caf50"
                description="Number of groups user belongs to"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricChart
                title="Recent Changes (7d)"
                dataKey="recentChanges7d"
                color="#ff9800"
                description="Access changes in the last 7 days"
              />
            </Grid>

            {/* Behavioral Metrics */}
            <Grid item xs={12} md={4}>
              <MetricChart
                title="Rapid Sign-ins (24h)"
                dataKey="rapidSignins24h"
                color="#e91e63"
                description="Number of rapid sign-ins in 24 hours"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricChart
                title="Off-hours Sign-ins (7d)"
                dataKey="offHoursSignins7d"
                color="#9c27b0"
                description="Sign-ins during off-hours in 7 days"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricChart
                title="Access Velocity"
                dataKey="accessVelocity"
                color="#607d8b"
                description="Rate of access changes compared to baseline"
              />
            </Grid>

            {/* Risk Metrics */}
            <Grid item xs={12} md={4}>
              <MetricChart
                title="Suspicious Activity"
                dataKey="suspiciousActivity"
                color="#f44336"
                description="Suspicious activity score"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricChart
                title="Excessive Access"
                dataKey="excessiveAccess"
                color="#ff5722"
                description="Excessive access privileges score"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MetricChart
                title="Behavioral Score"
                dataKey="behavioralScore"
                color="#795548"
                description="Overall behavioral risk score"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setShowHistoricalModal(false)}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  function ScoreRing({ score = 0, severity = 'success' }) {
    const theme = useTheme();
    const max = score <= 10 ? 10 : 100;
    const value = Math.max(0, Math.min(100, (score / max) * 100));
    const colorMap = {
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      error: theme.palette.error.main,
    };
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress variant="determinate" value={100} size={68} thickness={5}
          sx={{ color: alpha(theme.palette.text.disabled, 0.2) }} />
        <CircularProgress variant="determinate" value={value} size={68} thickness={5}
          sx={{ color: colorMap[severity], position: 'absolute', left: 0 }} />
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{score}</Typography>
        </Box>
      </Box>
    );
  }

  const Highlight = ({ icon: Icon, label, value, tone = 'default', tooltip }) => (
    <Tooltip title={tooltip || ''} placement="top" arrow>
      <Chip
        size="small"
        icon={<Icon fontSize="small" />}
        label={`${label}: ${value}`}
        variant="outlined"
        sx={{
          borderColor: tone === 'danger' ? 'error.main' : tone === 'warn' ? 'warning.main' : 'divider',
          color: tone === 'danger' ? 'error.main' : tone === 'warn' ? 'warning.main' : 'text.secondary',
          '& .MuiChip-icon': { color: 'inherit' },
        }}
      />
    </Tooltip>
  );

  const MetricPill = ({ label, value }) => (
    <Chip size="small" label={`${label}: ${value}`} variant="outlined" sx={{ borderColor: 'divider' }} />
  );

  const Trend = ({ value = 0 }) => {
    const color = value > 0 ? 'warning.main' : value < 0 ? 'success.main' : 'text.disabled';
    const Icon = value > 0 ? ArrowUpwardRoundedIcon : value < 0 ? ArrowDownwardRoundedIcon : RemoveRoundedIcon;
    return <Icon sx={{ fontSize: 16, color }} />;
  };

  const InsufficientChip = ({ label }) => (
    <Tooltip title="No sufficient data yet" arrow>
      <Chip size="small" label={label} variant="outlined" sx={{ borderStyle: 'dashed', borderColor: 'divider', color: 'text.disabled' }} />
    </Tooltip>
  );

  function RawMetrics({ item }) {
    const ad = item.insight_details?.access_details || {};
    const bd = item.insight_details?.behavioral_details || {};
    const riskTop = Object.fromEntries(
      Object.entries(item).filter(([k, v]) => typeof v === 'number' && !['overall_risk_score'].includes(k))
    );
    return (
      <Accordion sx={{ background: 'transparent', boxShadow: 'none', borderTop: 1, borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Raw Metrics</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">Access</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {Object.entries(ad).map(([k, v]) => (
                  <MetricPill key={k} label={k} value={String(v)} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">Behavior</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {Object.entries(bd).map(([k, v]) => (
                  <MetricPill key={k} label={k} value={String(v)} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">Risk</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {Object.entries(riskTop).map(([k, v]) => (
                  <MetricPill key={k} label={k} value={String(v)} />
                ))}
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  }

  function InsightCard({ item, isHistorical = false }) {
    const theme = useTheme();
    const severity = getSeverity(item.risk_level);

    return (
      <Card
        sx={{
          p: 2.5,
          borderRadius: 2,
          background: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.3)
            : alpha('#ffffff', 0.6),
          backdropFilter: 'blur(8px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          boxShadow: `0 8px 30px ${alpha('#000', 0.08)}`,
        }}
      >
        <Grid container spacing={3} alignItems="flex-start">
          {/* Column 1: Risk Summary */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ScoreRing score={item.overall_risk_score ?? 0} severity={severity} />
              <Box>
                <Chip size="small" color={severity} label={(item.risk_level || 'minimal').toUpperCase()} sx={{ fontWeight: 700 }} />
                <Typography variant="body2" sx={{ mt: 0.5 }}>Score: {item.overall_risk_score ?? 'N/A'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Calculated {item.calculation_date || item.created_at?.split('T')[0] || '—'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Column 2: Highlights */}
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Highlights</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Highlight icon={CheckCircleOutlineRoundedIcon} label="Dormant" value={item.dormant_status ? 'Yes' : 'No'}
                tone={item.dormant_status ? 'warn' : 'default'} tooltip="Account appears inactive or rarely used." />
              <Highlight icon={TimelineRoundedIcon} label="Access velocity" value={item.access_change_velocity ?? 0}
                tone={(item.access_change_velocity ?? 0) > 10 ? 'warn' : 'default'} tooltip="Rate of access changes compared to baseline." />
              <Highlight icon={WarningAmberRoundedIcon} label="Suspicious" value={item.suspicious_activity ?? 0}
                tone={(item.suspicious_activity ?? 0) > 0 ? 'danger' : 'default'} tooltip="Detected suspicious patterns or anomalies." />
              <Highlight icon={AccessTimeRoundedIcon} label="Behavioral" value={item.behavioral_anomaly ?? 0}
                tone={(item.behavioral_anomaly ?? 0) > 0 ? 'warn' : 'default'} tooltip="Behavior deviates from expected patterns." />
              <Highlight icon={LockOutlinedIcon} label="Excessive access" value={item.excessive_access ?? 0}
                tone={(item.excessive_access ?? 0) > 0 ? 'danger' : 'default'} tooltip="Permissions exceed role needs." />
            </Box>
          </Grid>

          {/* Column 3: Details & Trends */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Access</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
                  <MetricPill label="Apps" value={item.insight_details?.access_details?.app_count ?? 0} />
                  <MetricPill label="Groups" value={item.insight_details?.access_details?.group_count ?? 0} />
                  <MetricPill label="Changes (7d)" value={item.insight_details?.access_details?.recent_changes_7d ?? 0} />
                  {/* Sparkline placeholder if no array provided */}
                  <Box sx={{ width: 80, height: 24, borderRadius: 1, bgcolor: 'action.hover', display: 'grid', placeItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">trend</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Behavior</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
                  <MetricPill label="Rapid sign-ins (24h)" value={item.insight_details?.behavioral_details?.rapid_signins_24h ?? 0} />
                  <Trend value={(item.insight_details?.behavioral_details?.rapid_signins_24h ?? 0)} />
                  <MetricPill label="Off-hours (7d)" value={item.insight_details?.behavioral_details?.off_hours_signins_7d ?? 0} />
                  <Trend value={(item.insight_details?.behavioral_details?.off_hours_signins_7d ?? 0)} />
                </Box>
              </Grid>

              {Array.isArray(item.insight_details?.insufficient_data_insights) && item.insight_details.insufficient_data_insights.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Insufficient Data</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    {item.insight_details.insufficient_data_insights.map((k) => (
                      <InsufficientChip key={k} label={k} />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>

        {/* Raw Metrics collapsible */}
        <Box sx={{ mt: 1 }}>
          <RawMetrics item={item} />
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Box className="mt-3" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Latest Insight */}
        <InsightCard item={latestInsight} />

        {/* Historical Data Button */}
        {hasHistoricalData && (
          <Card sx={{
            p: 2,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'primary.main',
            backgroundColor: 'rgba(25, 118, 210, 0.02)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }
          }}
          onClick={() => setShowHistoricalModal(true)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <HistoryIcon color="primary" />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    View Historical Insights
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sortedItems.length - 1} previous insights available with trend analysis
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="small"
                sx={{ borderRadius: 2 }}
                startIcon={<HistoryIcon />}
              >
                View History
              </Button>
            </Box>
          </Card>
        )}
      </Box>

      {/* Historical Data Modal */}
      <HistoricalDataModal />
    </>
  );
}
